<template>
  <div class="filters__category">
    <p v-if="disclaimer" class="filters__disclaimer mb-3 px-3">
      <span class="d-flex align-items-center">
        {{ disclaimer.label }}
        <vb-icon
          v-if="disclaimer.tooltip"
          :name="'info'"
          :color="sass('gray-500')"
          :size="16"
          class="ml-1 cursor-pointer"
          v-b-popover.hover.top="disclaimer.tooltip"
        />
      </span>
      <span v-if="disclaimer.description" class="filters__section__subtitle">
        {{ disclaimer.description }}
      </span>
    </p>

    <div
      class="filters__panels"
      :class="panel_wrap ? 'filters__panel-wrap mb-3' : null"
    >
      <p
        v-if="!header_inside"
        class="filters__category-title filters__section__title"
      >
        {{ category.label }}
      </p>

      <div
        v-for="(panel, panel_index) in panels"
        :key="`cat-${category.key}-panel-${panel_index}`"
        :class="[
          panel_wrap ? 'filters__panel' : 'filters__panel mb-3',
          panel_modifiers(panel),
        ]"
      >
        <p
          v-if="header_inside && panel_index === 0"
          class="filters__category-title filters__section__title mb-2"
        >
          <span class="d-flex align-items-center">
            {{ category.label }}
          </span>
        </p>

        <template v-for="node in panel.filters">
          <slot name="node" :node="node">
            <filter-node
              :key="`node-${node.id}`"
              :node="node"
              :selections="selections"
              :single_switch="single_switch"
              :bucket_transform="bucket_transform"
              :round_bounds="round_bounds"
              :unbounded_range="unbounded_range"
              :widget_key="widget_key"
              @change="(event) => $emit('change', event)"
            />
          </slot>
        </template>

        <filter-group
          v-for="sub_category in panel.sub_categories"
          :key="`group-${sub_category.id}`"
          :group="sub_category"
          :selections="selections"
          :single_switch="single_switch"
          :bucket_transform="bucket_transform"
          :round_bounds="round_bounds"
          :unbounded_range="unbounded_range"
          :widget_key="widget_key"
          @change="(event) => $emit('change', event)"
        >
          <template #node="slot_props">
            <slot name="node" v-bind="slot_props">
              <filter-node
                :key="`node-${slot_props.node.id}`"
                :node="slot_props.node"
                :selections="selections"
                :single_switch="single_switch"
                :bucket_transform="bucket_transform"
                :round_bounds="round_bounds"
                :unbounded_range="unbounded_range"
                :widget_key="widget_key"
                @change="(event) => $emit('change', event)"
              />
            </slot>
          </template>
        </filter-group>
      </div>
    </div>
  </div>
</template>

<script>
  import { group_nodes_in_panels, category_filters } from '@/utilities/filters';
  import FilterNode from '@components/blocks/filters/filter-node.vue';
  import FilterGroup from '@components/blocks/filters/filter-group.vue';

  export default {
    name: 'filter-panels',

    components: {
      'filter-node': FilterNode,
      'filter-group': FilterGroup,
    },

    props: {
      category: {
        type: Object,
        required: true,
      },
      selections: {
        type: Object,
        default: () => ({}),
      },
      merge_singles: {
        type: Boolean,
        default: false,
      },
      panel_wrap: {
        type: Boolean,
        default: false,
      },
      header_inside: {
        type: Boolean,
        default: false,
      },
      single_switch: {
        type: Boolean,
        default: false,
      },
      bucket_transform: {
        type: Function,
        default: null,
      },
      round_bounds: {
        type: Boolean,
        default: false,
      },
      unbounded_range: {
        type: Boolean,
        default: false,
      },
      widget_key: {
        type: [String, Number],
        default: 0,
      },
    },

    computed: {
      // the category's own filters first, then all its sub_categories in one panel
      panels() {
        const panels = group_nodes_in_panels(this.category.filters, {
          merge_singles: this.merge_singles,
        }).map((filters) => ({ filters, sub_categories: [] }));

        if (this.category.sub_categories.length) {
          panels.push({
            filters: [],
            sub_categories: this.category.sub_categories,
          });
        }

        return panels;
      },
      disclaimer() {
        return this.category.disclaimer || null;
      },
    },

    methods: {
      panel_modifiers(panel) {
        const filters = panel.filters.concat(
          panel.sub_categories.flatMap((sub_category) =>
            category_filters(sub_category)
          )
        );
        const keys = filters.map((node) =>
          String(node.filter_key).replace(/\./g, '-')
        );
        return [...new Set(keys)].map((key) => `filters__panel--${key}`);
      },
    },
  };
</script>

<style lang="scss">
  .filters__panel {
    border-bottom: 1px solid $gray-200;
    padding: $grid-gutter-width/2 0;

    @include media-breakpoint-up(xl) {
      padding: $grid-gutter-width/2;
    }

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;

      @include media-breakpoint-up(xl) {
        padding-bottom: $grid-gutter-width/3;
      }
    }

    &-wrap {
      background: $white;
      border-radius: $border-radius-lg * 2;
    }

    & .vb-check {
      margin-bottom: $grid-gutter-width/6;
    }
  }

  .filters__sub-category {
    &:not(:last-child) {
      margin-bottom: map-get($spacers, 3);
    }
  }

  .filters__disclaimer {
    & > span:first-child {
      font-weight: $vb-font-weight-semibold;
    }
  }

  .filters__section__title {
    display: block;
    text-align: left;
    width: 100%;
    margin-bottom: $grid-gutter-width / 3;

    & span:first-child {
      font-weight: $vb-font-weight-semibold;
    }

    &-sub {
      text-decoration: underline;
    }
  }

  .filters__section__subtitle {
    display: block;
    text-align: left;
    width: 100%;
    font-size: $font-size-sm;
    line-height: 1.2em;
    margin-top: 0.1em;
  }
</style>
